import React, { FC } from 'react';
import { useStoreContext } from '../App/StoreProvider';
import { GET_ALL_NOTIFICATIONS } from '../../graphql/client/queries';
import { NetworkStatus } from '@apollo/client';
import { Query } from '../_common';
import { Error, Loading } from '../_common';
import NotificationListItem from './NotificationListItem';
import { useParams } from 'react-router-dom';

interface LooseObject {
    [key: string]: any;
}

interface NotificationDetailsProps {
    history: any;
    match: any;
    setIsFetching: React.Dispatch<React.SetStateAction<boolean>>;
}

const PAGE_SIZE = 50;

const NotificationDetails: FC<NotificationDetailsProps> = (props: NotificationDetailsProps): React.ReactElement => {
    const [state, dispatch] = useStoreContext();
    const [notificationTypeFilter, setNotificationTypeFilter] = React.useState('');
    const {
        messageList: { filters, searchPhrase, dateFilter },
    } = state;
    const { regionIds, messageType, carrierIds } = filters;

    const abortController = React.useRef(new AbortController());
    const context = React.useMemo(()=> {
        return {
        fetchOptions: {
            signal: abortController.current.signal,
        },
    }},[abortController.current.signal]);

    const {messageId} = useParams()
    const messageIdVal:any = messageId;

    const driverId = messageIdVal ? parseInt(messageIdVal) : 0;

    const abortRequest = React.useCallback((): void => {
        abortController.current.abort();
    },[abortController.current]);

    const allNotificationsVariables = {
        input: {
            searchString: searchPhrase,
            dateFrom: filters.fromDate,
            dateTo: filters.toDate,
            filter: dateFilter,
            regionIds,
            carrierIds,
            pageSize: PAGE_SIZE,
            filterByOnLoad: false,
            filterByOnShift: false,
            filterByWaitOnLoad: false,
            driverId: driverId,
            notificationType: notificationTypeFilter,
        },
        offset: 0,
    }

    if (messageType.length > 0) {
        allNotificationsVariables['input']['filterByOnLoad'] =
            messageType.indexOf('filterByOnLoad') > -1 ? true : false;
        allNotificationsVariables['input']['filterByOnShift'] =
            messageType.indexOf('filterByOnShift') > -1 ? true : false;
        allNotificationsVariables['input']['filterByWaitOnLoad'] =
            messageType.indexOf('filterByWaitOnLoad') > -1 ? true : false;
    }



    return (
        <div className="Notification-List-Section">
            <div className="Notification-Filters-Header">
                <div className="row d-flex">
                    <div className="col-md-12 px-auto">
                        <div className="Notification-Filters-Container justify-content-between">
                            <div className="display-flex-33">
                                <input
                                    type="radio"
                                    name="notificationfilter"
                                    value=""
                                    checked={notificationTypeFilter == '' ? true : false}
                                    onChange={event => setNotificationTypeFilter(event.target.value)}
                                />
                                <label>All</label>
                            </div>

                            <div className="display-flex-33">
                                <input
                                    type="radio"
                                    name="notificationfilter"
                                    value="Driver"
                                    checked={notificationTypeFilter == 'Driver' ? true : false}
                                    onChange={event => setNotificationTypeFilter(event.target.value)}
                                />
                                <label>Driver Only</label>
                            </div>
                            <div className="display-flex-33">
                                <input
                                    type="radio"
                                    name="notificationfilter"
                                    value="Load"
                                    checked={notificationTypeFilter == 'Load' ? true : false}
                                    onChange={event => setNotificationTypeFilter(event.target.value)}
                                />
                                <label>Load Only</label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Query<LooseObject>
                query={GET_ALL_NOTIFICATIONS}
                variables={allNotificationsVariables}
                fetchPolicy="network-only"
                // nextFetchPolicy= "cache-first"
                context={context}
                // notifyOnNetworkStatusChange={true}
                pollInterval={120000}
            >
                {({ error, data, loading, fetchMore, refetch, networkStatus }): React.ReactElement | null => {

                    React.useEffect(() => {
                        // if(loading)
                        props.setIsFetching(loading);
                    }, [loading]);
                   
                    if (error) {
                        return <Error error={error} />;
                    }

                    const notificationList: LooseObject[] = [];
                    if (data && data.getAllNotifications && data.getAllNotifications.length !== 0) {
                        data.getAllNotifications.forEach((element: LooseObject | null): void => {
                            if (element !== null) {
                                notificationList.push(element);
                            }
                        });
                    }
                    const offset = data && data.getAllNotifications ? data.getAllNotifications.length / PAGE_SIZE : 0;

                    let isEndOfResults = false;
                    if (
                        offset % 1 !== 0 ||
                        (!isEndOfResults &&
                            data &&
                            data.getAllNotifications &&
                            data.getAllNotifications.length < PAGE_SIZE)
                    ) {
                        // If offset is not a whole number, the end of the results has been reached. (5/11/19) - KS
                        isEndOfResults = true;
                    }

                    return (
                        <NotificationListItem
                            abortRequest={abortRequest}
                            notifications={notificationList}
                            refetch={refetch}
                            isLoading={loading}
                            isEndOfResults={isEndOfResults}
                            history={history}
                            onLoadMore={() =>
                                fetchMore({
                                    variables: {
                                        offset,
                                    },
                                    updateQuery: (prev, { fetchMoreResult }) => {
                                        if (
                                            prev &&
                                            fetchMoreResult &&
                                            prev.getAllNotifications &&
                                            fetchMoreResult.getAllNotifications
                                        ) {
                                            return Object.assign({}, prev, {
                                                getAllNotifications: [
                                                    ...prev.getAllNotifications,
                                                    ...fetchMoreResult.getAllNotifications,
                                                ],
                                            });
                                        } else {
                                            return prev;
                                        }
                                    },
                                })
                            }
                        />
                    );
                }}
            </Query>
        </div>
    );
};

export default NotificationDetails;
